import React, { useContext, useEffect, useState } from "react";
import {
    Box,
    Button,
    Flex,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Grid,
    Input,
    Select,
} from "@chakra-ui/react";
import { appRequest } from "routes";
import { AppContext } from "AppProvider/AppContext";

type Props = {
    getCategories: (currentP?: number) => Promise<void>;
    setIsCreateModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    editData: {};
    onSubmit: (editedData: any) => Promise<void>;
};

export default function CreateCategory({
    getCategories,
    setIsCreateModalOpen,
    editData,
    onSubmit,
}: Props): JSX.Element {
    const [formData, setFormData] = useState<any>({
        name: "",
        description: "",
        order: 0,
        isActive: true,
    });

    const [errors, setErrors] = useState<Record<string, string>>({});
    const { toastNotify } = useContext(AppContext);

    // Populate form in edit mode
    useEffect(() => {
        if (editData && Object.keys(editData).length > 0) {
            setFormData(editData);
        }
    }, [editData]);

    // Validation
    const ValidateForm = (): boolean => {
        const newErrors: Record<string, string> = {};
        if (!formData.name) newErrors.name = "Name is required";
        if (formData.order === "" || isNaN(Number(formData.order))) {
            newErrors.order = "Order must be a number";
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    // Input changes
    const handleInputChange = (
        e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
    ) => {
        const { name, value } = e.target;

        if (name === "order") {
            setFormData((prev: any) => ({
                ...prev,
                order: Number(value),
            }));
            return;
        }

        if (name === "isActive") {
            setFormData((prev: any) => ({
                ...prev,
                isActive: value === "true",
            }));
            return;
        }

        setFormData((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Submit handler
    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        if (!ValidateForm()) return;

        try {
            if (Object.keys(editData).length === 0) {
                // Create mode
                const payload = {
                    name: formData.name,
                    description: formData.description,
                    order: formData.order,
                    isActive: formData.isActive,
                };

                const response: any = await appRequest("faqCategory", "create", payload);

                if (response.status === "success") {
                    toastNotify({
                        title: "Success",
                        message: "Category added successfully",
                        type: "success",
                    });
                    getCategories();
                    setIsCreateModalOpen(false);
                } else {
                    toastNotify({
                        title: "Error in adding Category",
                        message: response.message,
                        type: "error",
                    });
                }
            } else {
                // Edit mode
                await onSubmit(formData);
            }
        } catch (error: any) {
            console.error("Error in saving category", error.message);
        }
    };

    return (
        <Box mt={4}>
            <form onSubmit={handleSubmit}>
                <Grid
                    gap={6}
                    templateColumns={{ base: "1fr", md: "1fr 1fr" }}
                    mb={8}
                    p={5}
                    shadow="base"
                    rounded="lg"
                    borderWidth={1}
                >
                    {/* Name */}
                    <FormControl variant="floating" isInvalid={!!errors.name}>
                        <Input
                            id="name"
                            name="name"
                            placeholder=""
                            value={formData.name}
                            onChange={handleInputChange}
                        />
                        <FormLabel htmlFor="name">Name</FormLabel>
                        <FormErrorMessage>{errors.name}</FormErrorMessage>
                    </FormControl>

                    {/* Description */}
                    <FormControl variant="floating">
                        <Input
                            id="description"
                            name="description"
                            placeholder=""
                            value={formData.description}
                            onChange={handleInputChange}
                        />
                        <FormLabel htmlFor="description">Description</FormLabel>
                    </FormControl>

                    {/* Order */}
                    <FormControl variant="floating" isInvalid={!!errors.order}>
                        <Input
                            id="order"
                            name="order"
                            type="number"
                            placeholder=""
                            value={formData.order}
                            onChange={handleInputChange}
                        />
                        <FormLabel htmlFor="order">Order</FormLabel>
                        <FormErrorMessage>{errors.order}</FormErrorMessage>
                    </FormControl>

                    {/* Status */}
                    <FormControl variant="floating">
                        <Select
                            id="isActive"
                            name="isActive"
                            value={String(formData.isActive)}
                            onChange={handleInputChange}
                        >
                            <option value="true">Active</option>
                            <option value="false">Inactive</option>
                        </Select>
                        <FormLabel htmlFor="isActive">Status</FormLabel>
                    </FormControl>
                </Grid>

                {/* Submit Button */}
                <Flex justify="end" mt={4}>
                    <Button type="submit" colorScheme="teal">
                        {Object.keys(editData).length === 0 ? "Submit" : "Update"}
                    </Button>
                </Flex>
            </form>
        </Box>
    );
}
